// Live previews. They render the unsaved draft, so changes show before anyone else sees them.
// `spot` names what the admin is hovering or editing; matching parts get outlined.
import { useEffect, useRef } from 'react'
import {
  CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, FileText, Inbox, LayoutDashboard, Layers, Lock, Moon,
  Plus, RotateCw, Settings, Sun, X,
} from 'lucide-react'
import { isHex, sameColor } from './settingsModel'
import { pulse, useReducedMotion } from './settingsMotion'
import { Segmented } from './SettingsPrimitives'

const alpha = (hex, a) => (isHex(hex) ? `${hex}${Math.round(a * 255).toString(16).padStart(2, '0')}` : undefined)

const FADE = '[&_*]:transition-[background-color,border-color,color,outline-color,opacity] [&_*]:duration-300 motion-reduce:[&_*]:transition-none'
const RING = 'outline outline-2 outline-offset-2'
const RING_INSET = 'outline outline-2 outline-offset-[-3px]'

function useSpot(rootRef, spot) {
  const reduced = useReducedMotion()
  useEffect(() => {
    if (!spot || !rootRef.current) return
    const hits = [...rootRef.current.querySelectorAll('[data-hl]')].filter(el => el.dataset.hl.split(' ').includes(spot))
    pulse(hits, reduced)
  }, [rootRef, spot, reduced])

  // Returns the props for a highlightable element.
  return (keys, { inset = false } = {}) => {
    const list = keys.filter(Boolean)
    const on = list.includes(spot)
    return {
      'data-hl': list.join(' '),
      className: `${inset ? RING_INSET : RING} ${on ? 'outline-sky-400' : 'outline-transparent'}`,
    }
  }
}

const cx = (...parts) => parts.filter(Boolean).join(' ')

export function PreviewFrame({ dark, onDarkChange, children }) {
  return (
    <div>
      <div className="mb-3 flex h-8 items-center justify-between">
        <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400">Preview</p>
        {onDarkChange && (
          <Segmented
            size="sm"
            ariaLabel="Preview theme"
            value={dark ? 'dark' : 'light'}
            onChange={v => onDarkChange(v === 'dark')}
            options={[
              { value: 'light', icon: Sun, ariaLabel: 'Light' },
              { value: 'dark', icon: Moon, ariaLabel: 'Dark' },
            ]}
          />
        )}
      </div>
      {children}
    </div>
  )
}

// Web app

const LIGHT = {
  chrome: '#e9ebee', tab: '#ffffff', bar: '#ffffff', address: '#f1f3f5', canvas: '#f9fafb', card: '#ffffff',
  border: '#e5e7eb', divider: '#f3f4f6', text: '#111827', sub: '#6b7280', muted: '#9ca3af', sidebar: null,
}
const DARK = {
  chrome: '#141414', tab: '#262626', bar: '#1d1d1d', address: '#2a2a2a', canvas: '#121212', card: '#1d1d1d',
  border: '#333333', divider: '#262626', text: '#ededed', sub: '#a8a8a8', muted: '#8c8c8c', sidebar: '#1d1d1d',
}

const NAV = [
  { label: 'Dashboard', icon: LayoutDashboard },
  { label: 'Requests', icon: Inbox, active: true },
  { label: 'Templates', icon: Layers },
  { label: 'Approvals', icon: CheckCircle2 },
  { label: 'Settings', icon: Settings },
]

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  return (parts[0]?.[0] || 'A').toUpperCase() + (parts[1]?.[0] || '').toUpperCase()
}

export function AppPreview({ draft, tokens, tokenDefaults, host, user, dark, spot }) {
  const rootRef = useRef(null)
  const hl = useSpot(rootRef, spot)
  const c = dark ? DARK : LIGHT

  const brand = isHex(draft.primary_color) ? draft.primary_color : '#1e2a3b'
  const tk = key => (isHex(tokens[key]) ? tokens[key] : tokenDefaults[key])
  // Same rule as BrandingContext: an untouched "toggle on" follows the brand colour.
  const followsBrand = sameColor(tokens.toggle_on, tokenDefaults.toggle_on) || !isHex(tokens.toggle_on)
  const toggleOn = followsBrand ? brand : tokens.toggle_on
  const primary = spot === 'primary_hover' ? tk('primary_hover') : tk('primary')
  const primaryText = tk('primary_text')
  const sidebar = c.sidebar || brand

  const rows = [
    { id: 'INV-2026-00142', who: 'Acme Corporation', label: 'Approved', key: 'success' },
    { id: 'PO-88213', who: 'Northwind Traders', label: 'In review', key: 'warning' },
    { id: 'INV-2026-00139', who: 'Contoso Ltd', label: 'Rejected', key: 'error' },
    { id: 'BID-4471', who: 'Fabrikam Inc', label: 'Processing', key: 'info' },
  ]

  const name = hl(['name'])
  const subtitle = hl(['subtitle'])
  const logo = hl(['logo'])
  const favicon = hl(['favicon'])
  const bar = hl(['host'], { inset: true })
  const side = hl([!dark && 'brand'], { inset: true })
  const button = hl(['primary', 'primary_hover', 'primary_text'])
  const tabOn = hl(['primary', 'primary_text'])
  const on = hl(['toggle_on', followsBrand && 'brand'])
  const off = hl(['toggle_off'])

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className={cx('select-none overflow-hidden rounded-xl shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] ring-1 ring-black/10 dark:ring-white/10', FADE)}
    >
      {/* Tab strip */}
      <div className="flex items-end gap-2 px-2.5 pt-2" style={{ backgroundColor: c.chrome }}>
        <div className="flex gap-1.5 pb-2.5 pl-0.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </div>
        <div className="flex min-w-0 max-w-[64%] items-center gap-1.5 rounded-t-lg px-2.5 py-1.5" style={{ backgroundColor: c.tab }}>
          <span data-hl={favicon['data-hl']} className={cx('shrink-0 rounded-sm', favicon.className)}>
            {draft.favicon_url
              ? <img key={draft.favicon_url} src={draft.favicon_url} alt="" className="h-3.5 w-3.5 object-contain animate-panel-in" />
              : <span className="block h-3.5 w-3.5 rounded-sm" style={{ backgroundColor: brand }} />}
          </span>
          <span data-hl={name['data-hl']} className={cx('truncate rounded-sm text-[10.5px] font-medium', name.className)} style={{ color: c.text }}>
            {draft.app_name || 'Xtract'}
          </span>
          <X className="ml-1 h-2.5 w-2.5 shrink-0" style={{ color: c.muted }} />
        </div>
      </div>

      {/* Toolbar */}
      <div data-hl={bar['data-hl']} className={cx('flex items-center gap-2 px-2.5 py-1.5', bar.className)} style={{ backgroundColor: c.bar, borderBottom: `1px solid ${c.border}` }}>
        <span className="flex gap-1" style={{ color: c.muted }}>
          <ChevronLeft className="h-3 w-3" />
          <ChevronRight className="h-3 w-3" />
          <RotateCw className="h-2.5 w-2.5 translate-y-px" />
        </span>
        <span className="flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1" style={{ backgroundColor: c.address }}>
          <Lock className="h-2.5 w-2.5 shrink-0" style={{ color: c.sub }} />
          <span key={host} className="truncate text-[10px] animate-panel-in" style={{ color: c.sub }}>{host}</span>
        </span>
        <span className="w-9" />
      </div>

      <div className="flex h-[322px]" style={{ backgroundColor: c.canvas }}>
        {/* Sidebar */}
        <div data-hl={side['data-hl']} className={cx('flex w-[38%] flex-col p-2.5', side.className)} style={{ backgroundColor: sidebar }}>
          <div className="flex items-center gap-2 border-b border-white/10 pb-2.5">
            <span data-hl={logo['data-hl']} className={cx('shrink-0 rounded', logo.className)}>
              {draft.logo_url
                ? <img key={draft.logo_url} src={draft.logo_url} alt="" className="h-6 w-6 rounded object-contain animate-panel-in" />
                : <span className="block h-6 w-6 rounded bg-white/20" />}
            </span>
            <div className="min-w-0">
              <p data-hl={name['data-hl']} className={cx('truncate rounded-sm text-[11px] font-semibold leading-tight text-white', name.className)}>
                {draft.app_name || 'Xtract'}
              </p>
              <p data-hl={subtitle['data-hl']} className={cx('truncate rounded-sm text-[9px] text-white/50', subtitle.className)}>
                {draft.subtitle || 'Synapx'}
              </p>
            </div>
          </div>
          <div className="mt-2.5 flex-1 space-y-0.5">
            {NAV.map(({ label, icon: Icon, active }) => (
              <div
                key={label}
                className={cx('relative flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[10px] font-medium', active ? 'bg-white/15 text-white' : 'text-white/55')}
              >
                {active && <span className="absolute bottom-1.5 left-0 top-1.5 w-0.5 rounded-full bg-white/80" />}
                <Icon className="h-3 w-3 shrink-0" />
                <span className="truncate">{label}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 border-t border-white/10 pt-2">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/15 text-[8px] font-semibold text-white">
              {initials(user?.name)}
            </span>
            <span className="truncate text-[9px] text-white/65">{user?.name || 'Admin'}</span>
          </div>
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1 space-y-2.5 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12px] font-bold" style={{ color: c.text }}>Requests</p>
            <span
              data-hl={button['data-hl']}
              className={cx('inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9.5px] font-semibold shadow-sm', button.className)}
              style={{ backgroundColor: primary, color: primaryText }}
            >
              <Plus className="h-2.5 w-2.5" /> New request
            </span>
          </div>
          <div className="flex gap-1">
            {['All', 'Mine', 'Review'].map((tab, i) => (
              <span
                key={tab}
                data-hl={i === 0 ? tabOn['data-hl'] : undefined}
                className={cx('rounded px-1.5 py-0.5 text-[9px] font-medium', i === 0 && tabOn.className)}
                style={i === 0 ? { backgroundColor: tk('primary'), color: primaryText } : { color: c.sub }}
              >
                {tab}
              </span>
            ))}
          </div>
          <div className="rounded-lg" style={{ backgroundColor: c.card, border: `1px solid ${c.border}` }}>
            {rows.map((row, i) => {
              const color = tk(row.key)
              const badge = hl([row.key])
              return (
                <div
                  key={row.id}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5"
                  style={i ? { borderTop: `1px solid ${c.divider}` } : undefined}
                >
                  <div className="min-w-0">
                    <p className="truncate text-[9.5px] font-semibold" style={{ color: c.text }}>{row.id}</p>
                    <p className="truncate text-[8.5px]" style={{ color: c.muted }}>{row.who}</p>
                  </div>
                  <span
                    data-hl={badge['data-hl']}
                    className={cx('inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[8.5px] font-semibold', badge.className)}
                    style={{ backgroundColor: alpha(color, dark ? 0.2 : 0.13), color }}
                  >
                    <span className="h-1 w-1 rounded-full" style={{ backgroundColor: color }} />
                    {row.label}
                  </span>
                </div>
              )
            })}
          </div>
          {[
            { label: 'Notify approvers', checked: true, color: toggleOn, props: on },
            { label: 'Daily digest', checked: false, color: tk('toggle_off'), props: off },
          ].map(item => (
            <div
              key={item.label}
              className="flex items-center justify-between rounded-lg px-2.5 py-2"
              style={{ backgroundColor: c.card, border: `1px solid ${c.border}` }}
            >
              <span className="text-[9.5px] font-medium" style={{ color: c.text }}>{item.label}</span>
              <span
                data-hl={item.props['data-hl']}
                className={cx('relative inline-block h-3 w-[22px] rounded-full', item.props.className)}
                style={{ backgroundColor: item.color }}
              >
                <span className={cx('absolute top-0.5 h-2 w-2 rounded-full bg-white shadow', item.checked ? 'right-0.5' : 'left-0.5')} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Outlook add-in

const FILES = [
  { name: 'Invoice-00142.pdf', size: '245 KB', tint: 'linear-gradient(135deg,#ff6b6b,#ee5a5a)' },
  { name: 'Purchase-order.pdf', size: '1.2 MB', tint: 'linear-gradient(135deg,#4facfe,#00f2fe)' },
  { name: 'Remittance.xlsx', size: '89 KB', tint: 'linear-gradient(135deg,#43e97b,#38f9d7)' },
]

export function PluginPreview({ config, bodyText, dark, spot }) {
  const rootRef = useRef(null)
  const hl = useSpot(rootRef, spot)
  const c = dark
    ? { bg: '#141414', card: '#1e1e1e', text: '#e5e7eb', sub: '#9ca3af', muted: '#6b7280', border: '#2e2e2e' }
    : { bg: '#f9fafb', card: '#ffffff', text: '#1f2937', sub: '#6b7280', muted: '#9ca3af', border: '#e5e7eb' }
  const accent = isHex(config.primary_color) ? config.primary_color : '#1e2a3b'

  const header = hl(['plugin-mode'], { inset: true })
  const logo = hl(['plugin-logo'])
  const name = hl(['plugin-name'])
  const body = hl(['plugin-body'])

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className={cx('select-none overflow-hidden rounded-xl shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] ring-1 ring-black/10 dark:ring-white/10', FADE)}
      style={{ backgroundColor: c.bg }}
    >
      <div
        data-hl={header['data-hl']}
        className={cx('flex items-center gap-2 px-4 py-3', header.className)}
        style={{ backgroundColor: c.card, borderBottom: `1px solid ${c.border}` }}
      >
        <span data-hl={logo['data-hl']} className={cx('shrink-0 rounded', logo.className)}>
          {config.logo_url
            ? <img key={config.logo_url} src={config.logo_url} alt="" className="h-6 w-6 rounded object-contain animate-panel-in" />
            : <span className="block h-6 w-6 rounded" style={{ backgroundColor: alpha(accent, 0.2) }} />}
        </span>
        <span data-hl={name['data-hl']} className={cx('truncate rounded-sm text-[13px] font-bold', name.className)} style={{ color: c.text }}>
          {config.app_name || 'Xtract'}
        </span>
      </div>

      <div className="space-y-2.5 px-4 py-3.5">
        <p data-hl={body['data-hl']} className={cx('rounded-sm text-[11px] leading-relaxed', body.className)} style={{ color: c.sub }}>
          {bodyText || 'Select attachments to process and click Submit.'}
        </p>
        <p className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider" style={{ color: c.sub }}>
          <span className="inline-block h-3 w-[3px] rounded-sm" style={{ backgroundColor: accent }} />
          Select files
        </p>
        {FILES.map((file, i) => (
          <div key={file.name} className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ backgroundColor: c.card, border: `1px solid ${c.border}` }}>
            <span
              className="grid h-3.5 w-3.5 place-items-center rounded"
              style={i === 0 ? { backgroundColor: accent } : { border: `2px solid ${c.border}` }}
            >
              {i === 0 && <CheckCircle2 className="h-2.5 w-2.5 text-white" />}
            </span>
            <span className="grid h-5 w-5 place-items-center rounded" style={{ background: file.tint }}>
              <FileText className="h-3 w-3 text-white" />
            </span>
            <span className="truncate text-[11px] font-medium" style={{ color: c.text }}>{file.name}</span>
            <span className="ml-auto shrink-0 text-[10px]" style={{ color: c.muted }}>{file.size}</span>
          </div>
        ))}
        <div className="w-full rounded-lg py-2 text-center text-[11.5px] font-semibold text-white" style={{ backgroundColor: accent }}>
          Submit
        </div>
        <div className="pt-2" style={{ borderTop: `1px solid ${c.border}` }}>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-[9px] font-semibold uppercase" style={{ color: c.sub }}>
              <ClipboardList className="h-3 w-3" /> Related requests
            </span>
            <span className="grid h-4 w-4 place-items-center rounded-full text-[8px] font-bold text-white" style={{ backgroundColor: accent }}>2</span>
          </div>
          <div className="mt-1.5 space-y-1 text-[9.5px]" style={{ color: c.muted }}>
            <p>#275 · Pending review · 14/04/2026</p>
            <p>#274 · Extracted · 12/04/2026</p>
          </div>
        </div>
      </div>
      <div className="py-2 text-center text-[10px] font-medium text-white" style={{ backgroundColor: accent }}>
        Copyright © {new Date().getFullYear()} {config.app_name || 'Xtract'}
      </div>
    </div>
  )
}

// Request view

const SAMPLE_FIELDS = [
  { name: 'Invoice number', value: 'INV-2026-00142', confidence: 0.96, llm: false },
  { name: 'Vendor name', value: 'Acme Corporation Ltd', confidence: 0.78, llm: true },
  { name: 'Due date', value: '15/06/2026', confidence: 0.64, llm: true },
  { name: 'Total amount', value: 'GBP 12,450.00', confidence: 0.92, llm: false },
]

export function DataDisplayPreview({ showConfidenceScore, showAiBadge, showColorCoding, spot }) {
  const rootRef = useRef(null)
  const hl = useSpot(rootRef, spot)

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="select-none overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.3)] dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="flex items-center gap-2.5 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-white ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
          <FileText className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
        </span>
        <div>
          <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">Request #275</p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Extracted fields</p>
        </div>
      </div>
      <div className="space-y-3.5 p-4">
        {SAMPLE_FIELDS.map(field => {
          const pct = Math.round(field.confidence * 100)
          const high = field.confidence >= 0.9
          const med = field.confidence >= 0.7 && !high
          const low = field.confidence < 0.7
          const badge = high
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : med
              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          const box = showColorCoding && low
            ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
            : showColorCoding && med
              ? 'border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20'
              : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800'
          const score = hl(['confidence'])
          const ai = hl(['ai'])
          const colour = hl(['colour'])

          return (
            <div key={field.name}>
              <div className="mb-1 flex h-5 items-center">
                <span className={cx('h-2 shrink-0 rounded-full transition-all duration-300',
                  showColorCoding ? 'mr-1.5 w-2 opacity-100' : 'mr-0 w-0 opacity-0',
                  high ? 'bg-green-500' : med ? 'bg-orange-500' : 'bg-red-500')} />
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400">{field.name}</span>
                <span
                  data-hl={score['data-hl']}
                  className={cx('overflow-hidden whitespace-nowrap rounded py-0.5 text-[10.5px] transition-all duration-300', badge, score.className,
                    showConfidenceScore ? 'ml-1.5 max-w-[50px] px-1.5 opacity-100' : 'ml-0 max-w-0 px-0 opacity-0')}
                >
                  {pct}%
                </span>
                {field.llm && (
                  <span
                    data-hl={ai['data-hl']}
                    className={cx('overflow-hidden whitespace-nowrap rounded bg-purple-100 py-0.5 text-[10.5px] text-purple-700 transition-all duration-300',
                      'dark:bg-purple-900/30 dark:text-purple-400', ai.className,
                      showAiBadge ? 'ml-1.5 max-w-[30px] px-1.5 opacity-100' : 'ml-0 max-w-0 px-0 opacity-0')}
                  >
                    AI
                  </span>
                )}
                {low && (
                  <span className={cx('overflow-hidden whitespace-nowrap text-[10.5px] italic text-red-500 transition-all duration-300 dark:text-red-400',
                    showColorCoding && showConfidenceScore ? 'ml-1.5 max-w-[120px] opacity-100' : 'ml-0 max-w-0 opacity-0')}
                  >
                    Low confidence
                  </span>
                )}
              </div>
              <div
                data-hl={showColorCoding && !high ? colour['data-hl'] : undefined}
                className={cx('w-full rounded-lg border px-3 py-2 text-[13px] font-medium text-gray-900 transition-colors duration-300 dark:text-gray-200',
                  box, showColorCoding && !high ? colour.className : '')}
              >
                {field.value}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
